/**
 * Shared live quiz / grasp types (CodeLive).
 *
 * Flow:
 *   step 1) ActiveLiveQuiz pushed to students
 *   step 2) LiveQuizSubmission scored + fanned out
 *   step 3) GraspVote after post-quiz
 */

export type QuizSlot = "pre" | "post";

export type QuizTemplate = "single_choice" | "multiple_choice";

export type LiveQuizQuestion = {
  id: string;
  text: string;
  difficulty: string;
  options: string[];
  correct_index: number;
  template: QuizTemplate;
  correct_indices: number[];
};

export type ActiveLiveQuiz = {
  push_id: string;
  slot: QuizSlot;
  topic_id: string;
  topic_title: string;
  topic_index: number;
  questions: LiveQuizQuestion[];
  duration_seconds: number;
  pushed_at: number;
  /** Server clock when this payload was built. Absent on an older API. */
  server_now_ms?: number;
  status: "active" | "completed" | "aborted";
};

/** True only while status is active and the quiz timer has not elapsed. */
export function isLiveQuiz(
  quiz: Pick<
    ActiveLiveQuiz,
    "status" | "pushed_at" | "duration_seconds" | "server_now_ms"
  > | null | undefined,
  nowMs: number = Date.now(),
): boolean {
  if (!quiz || quiz.status !== "active") return false;
  const pushed = Number(quiz.pushed_at || 0);
  const duration = Number(quiz.duration_seconds || 0);
  if (pushed <= 0 || duration <= 0) return quiz.status === "active";
  const serverNow = Number(quiz.server_now_ms || 0);
  const clock = serverNow > 0 ? serverNow : nowMs;
  return clock < pushed + duration * 1000;
}

export type LiveQuizSubmission = {
  push_id: string;
  student_id: string;
  student_name: string;
  answers: number[][];
  score: number;
  max_score: number;
  submitted_at: number;
  timed_out?: boolean;
  topic_id?: string;
  topic_title?: string;
  slot?: QuizSlot;
  questions?: LiveQuizQuestion[];
  missed?: boolean;
  /** True when this submit caused the live quiz to auto-complete. */
  quiz_completed?: boolean;
};

/** One student's quiz history row (Meeting Tools / analytics popup). */
export type StudentQuizHistoryItem = LiveQuizSubmission;

export type GraspLevel =
  | "understood"
  | "partial"
  | "not_understood"
  | "not_answered";

export const GRASP_LABELS: Record<GraspLevel, string> = {
  understood: "Understood",
  partial: "Partially Understood",
  not_understood: "Did Not Understand",
  not_answered: "Not Answered",
};

export type GraspVote = {
  student_id: string;
  student_name: string;
  topic_id: string;
  topic_title: string;
  level: GraspLevel;
  message?: string | null;
  unclear_subtopics?: string[];
  confusion_tags?: string[];
  submitted_at: number;
  trainer_answer?: string | null;
  status?: string;
  closed_at?: number;
};

/** Normalize grasp vote from REST / LiveKit (snake or camel). */
export function normalizeGraspVote(
  raw: Record<string, unknown> | GraspVote | null | undefined,
): GraspVote | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const level = String(r.level ?? "");
  if (
    level !== "understood" &&
    level !== "partial" &&
    level !== "not_understood" &&
    level !== "not_answered"
  ) {
    return null;
  }
  const asStringList = (value: unknown): string[] => {
    if (Array.isArray(value)) {
      return value.map((x) => String(x || "").trim()).filter(Boolean);
    }
    if (typeof value === "string" && value.trim()) {
      return [value.trim()];
    }
    return [];
  };
  const trainerAnswerRaw = r.trainer_answer ?? r.trainerAnswer;
  return {
    student_id: String(r.student_id ?? r.studentId ?? ""),
    student_name: String(r.student_name ?? r.studentName ?? ""),
    topic_id: String(r.topic_id ?? r.topicId ?? ""),
    topic_title: String(r.topic_title ?? r.topicTitle ?? ""),
    level: level as GraspLevel,
    message: String(r.message ?? "").trim() || null,
    unclear_subtopics: asStringList(
      r.unclear_subtopics ?? r.unclearSubtopics,
    ),
    confusion_tags: asStringList(r.confusion_tags ?? r.confusionTags),
    submitted_at: Number(r.submitted_at ?? r.submittedAt ?? 0),
    trainer_answer:
      trainerAnswerRaw != null && String(trainerAnswerRaw).trim()
        ? String(trainerAnswerRaw).trim()
        : null,
    status: String(r.status ?? "open").trim().toLowerCase() || "open",
    closed_at: Number(r.closed_at ?? r.closedAt ?? 0) || 0,
  };
}

export const LIVE_QUIZ_PUSH_TYPE = "codelive.quiz-push" as const;
export const LIVE_QUIZ_SUBMIT_TYPE = "codelive.quiz-submit" as const;
export const LIVE_GRASP_TYPE = "codelive.grasp-vote" as const;
/** Trainer skipped / has no post-quiz → open student GraspFeedback dialog. */
export const LIVE_GRASP_PROMPT_TYPE = "codelive.grasp-prompt" as const;

export type LiveQuizPushMessage = {
  type: typeof LIVE_QUIZ_PUSH_TYPE;
  v: 1;
  quiz: ActiveLiveQuiz;
};

export type LiveQuizSubmitMessage = {
  type: typeof LIVE_QUIZ_SUBMIT_TYPE;
  v: 1;
  submission: LiveQuizSubmission;
};

export type LiveGraspMessage = {
  type: typeof LIVE_GRASP_TYPE;
  v: 1;
  vote: GraspVote;
};

export type LiveGraspPrompt = {
  topic_id: string;
  topic_title: string;
};

export type LiveGraspPromptMessage = {
  type: typeof LIVE_GRASP_PROMPT_TYPE;
  v: 1;
  prompt: LiveGraspPrompt;
};

/** Normalize one answer slot from API (legacy int or int[]). */
export function normalizeAnswerSlot(raw: unknown): number[] {
  if (typeof raw === "number" && Number.isFinite(raw) && raw >= 0) {
    return [raw];
  }
  if (Array.isArray(raw)) {
    return raw
      .map((v) => Number(v))
      .filter((v) => Number.isFinite(v) && v >= 0);
  }
  return [];
}

export function normalizeCorrectIndices(q: Record<string, unknown>): number[] {
  const raw = q.correct_indices ?? q.correctIndices;
  if (Array.isArray(raw) && raw.length > 0) {
    return raw.map((v) => Number(v)).filter((v) => Number.isFinite(v) && v >= 0);
  }
  const single = Number(q.correct_index ?? q.correctIndex ?? 0);
  return Number.isFinite(single) && single >= 0 ? [single] : [0];
}

export function normalizeLiveQuizQuestion(
  q: Record<string, unknown>,
): LiveQuizQuestion {
  const correct_indices = normalizeCorrectIndices(q);
  const templateRaw = String(q.template ?? "single_choice")
    .trim()
    .toLowerCase();
  return {
    id: String(q.id ?? ""),
    text: String(q.text ?? ""),
    difficulty: String(q.difficulty ?? "Medium"),
    options: Array.isArray(q.options) ? q.options.map(String) : [],
    correct_index: correct_indices[0] ?? 0,
    template:
      templateRaw === "multiple_choice" ? "multiple_choice" : "single_choice",
    correct_indices,
  };
}

export function setsEqual(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  const left = new Set(a);
  const right = new Set(b);
  if (left.size !== right.size) return false;
  for (const v of left) {
    if (!right.has(v)) return false;
  }
  return true;
}
